import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/Card/card";
import Icon from "@/components/Icon/Icon";
import { Grid, HStack, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { colSpanClasses } from "@/constants/colSpan";
import { cn } from "@/lib/cn";
import { type HTMLAttributes, forwardRef } from "react";
import type { AttributeCardProps } from "./types";

const AttributeCard = forwardRef<HTMLDivElement, AttributeCardProps>(
  function AttributeCard(props, ref) {
    const {
      title,
      titleElement,
      titleStyle = "label",
      description,
      icon,
      iconSize,
      colSpan = 1,
      actionButton,
      actionButtonPosition = "top-right",
      actionButtonIsInner = true,
      className,
      children,
      headerClassName,
      contentClassName,
      ...rest
    } = props;

    return (
      <Card
        ref={ref}
        className={cn(
          "flex overflow-auto flex-col",
          colSpanClasses[colSpan],
          className,
        )}
        {...rest}
      >
        {actionButtonIsInner ? (
          <>
            {title && (
              <CardHeader className={cn("pt-5", headerClassName)}>
                <CardTitle>
                  <HStack className="justify-between items-center h-10">
                    {actionButton && actionButtonPosition === "top-left" && (
                      <div>{actionButton}</div>
                    )}
                    <VStack>
                      {icon ? (
                        <Icon iconName={icon} size={iconSize} text={title} />
                      ) : (
                        <Text variant={titleStyle}>{title}</Text>
                      )}
                      {description && (
                        <CardDescription>{description} </CardDescription>
                      )}
                    </VStack>
                    {actionButton && actionButtonPosition === "top-right" && (
                      <div>{actionButton}</div>
                    )}
                  </HStack>
                </CardTitle>
              </CardHeader>
            )}
            {titleElement && (
              <CardHeader className={cn("pt-5", headerClassName)}>
                <CardTitle>{titleElement}</CardTitle>
              </CardHeader>
            )}
            <CardContent className={contentClassName}>
              <VStack>
                {children}
                {actionButton &&
                  (actionButtonPosition === "bottom-right" ||
                    actionButtonPosition === "bottom-left") && (
                    <div
                      className={cn(
                        "mt-4",
                        actionButtonPosition === "bottom-right"
                          ? "self-end"
                          : "self-start",
                      )}
                    >
                      {actionButton}
                    </div>
                  )}
              </VStack>
            </CardContent>
          </>
        ) : (
          <Grid
            columns={3}
            gridTemplateColumns={
              actionButtonPosition === "top-left" ? "36px 1fr" : "1fr 36px"
            }
          >
            {actionButton && actionButtonPosition === "top-left" && (
              <VStack>
                <Spacer variant="m" />
                <HStack>
                  <Spacer variant="m" />
                  {actionButton}
                </HStack>
              </VStack>
            )}
            <VStack>
              <CardHeader className={cn("pt-5", headerClassName)}>
                <CardTitle>
                  <HStack className="items-center">
                    {icon ? (
                      <Icon iconName={icon} size={iconSize} />
                    ) : (
                      <Text variant={titleStyle}>{title}</Text>
                    )}
                  </HStack>
                  {description && (
                    <CardDescription>{description} </CardDescription>
                  )}
                </CardTitle>
              </CardHeader>
              {titleElement && (
                <CardHeader className={cn("pt-5", headerClassName)}>
                  <CardTitle>{titleElement}</CardTitle>
                </CardHeader>
              )}
              <CardContent className={contentClassName}>
                <VStack className="gap-1">
                  {children}
                  {actionButton &&
                    (actionButtonPosition === "bottom-right" ||
                      actionButtonPosition === "bottom-left") && (
                      <div
                        className={cn(
                          "mt-4",
                          actionButtonPosition === "bottom-right"
                            ? "self-end"
                            : "self-start",
                        )}
                      >
                        {actionButton}
                      </div>
                    )}
                </VStack>
              </CardContent>
            </VStack>
            {actionButton && actionButtonPosition === "top-right" && (
              <VStack>
                <Spacer variant="m" />
                <HStack>
                  {actionButton}
                  <Spacer variant="m" />
                </HStack>
              </VStack>
            )}
          </Grid>
        )}
      </Card>
    );
  },
);

type AttributeCardCaptionProps = HTMLAttributes<HTMLSpanElement>;

function AttributeCardCaption({
  className,
  ...props
}: AttributeCardCaptionProps) {
  return (
    <Text
      as="span"
      variant="labelSmall"
      className={cn("text-neutral-muted-on-fill", className)}
      {...props}
    />
  );
}

type AttributeCardValueProps = HTMLAttributes<HTMLSpanElement>;

function AttributeCardValue({ className, ...props }: AttributeCardValueProps) {
  return (
    <Text
      as="span"
      variant="labelLarge"
      className={cn("flex items-center", className)}
      {...props}
    />
  );
}

export { AttributeCard, AttributeCardCaption, AttributeCardValue };
